// Wave 20.49 — user-configurable Mapbox basemap for RouteMap.
//
// Same pattern as useUnitSystem: localStorage-backed with a
// cross-tab StorageEvent sync so hooks re-render when the pref
// changes anywhere.
//
// Options are limited to four hand-picked styles that all pair
// well with the brand-green polyline and (except dark) show
// terrain shading legibly.

import { useEffect, useState } from 'react';

export type MapStyle =
  | 'outdoors-v12'      // default — Mapbox Outdoors, has hillshade + labels
  | 'satellite-streets-v12'
  | 'streets-v12'
  | 'dark-v11';         // original dark theme (harder to read terrain)

export const MAP_STYLE_LABELS: Record<MapStyle, string> = {
  'outdoors-v12': 'Outdoors',
  'satellite-streets-v12': 'Satellite',
  'streets-v12': 'Streets',
  'dark-v11': 'Dark',
};

export const MAP_STYLE_DESCRIPTIONS: Record<MapStyle, string> = {
  'outdoors-v12': 'Bright topo with visible hillshade and trails.',
  'satellite-streets-v12': 'Real satellite imagery with road overlays.',
  'streets-v12': 'Clean daytime street map.',
  'dark-v11': 'High-contrast night map (harder to read terrain).',
};

const KEY = 'aero.chat_mapstyle';
const DEFAULT: MapStyle = 'outdoors-v12';

function read(): MapStyle {
  try {
    const v = localStorage.getItem(KEY);
    if (
      v === 'outdoors-v12' ||
      v === 'satellite-streets-v12' ||
      v === 'streets-v12' ||
      v === 'dark-v11'
    ) {
      return v;
    }
    return DEFAULT;
  } catch {
    return DEFAULT;
  }
}

export function useMapStyle(): MapStyle {
  const [value, setValue] = useState<MapStyle>(read);
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setValue(read());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return value;
}

export function setMapStyle(next: MapStyle) {
  try {
    localStorage.setItem(KEY, next);
    window.dispatchEvent(new StorageEvent('storage', { key: KEY, newValue: next }));
  } catch {
    /* private mode — noop */
  }
}

export function mapStyleUrl(s: MapStyle): string {
  return `mapbox://styles/mapbox/${s}`;
}
